import React, { useEffect, useState, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import classNames from 'classnames';
import { FormattingTextarea } from 'react-telegram-input-text-entities';
import { Input } from '@/components/Form/Input';
import { humanizeDuration, lifetime } from '@/utils/durations';
import { GenericForm } from '@/components/Form/GenericForm';
import { fetchAPI } from '@/api';
import { OutletContext, Resource } from '@/types';
import DropdownSelect from '@/components/DropdownSelect';
import {
    availableFiatCurrencies, availableCryptoCurrencies, availableOtherCurrencies, getCurrencyCodeTitle,
    // @ts-ignore
} from 'invitemember-utils-public/currencies';
import { priceView } from '@/utils/utils';

export function PlanForm({ formData, onFieldChange, onSave, formReset, isLoading, formRef, plan, isDuplicating = false }: any) {
    const { project, auth } = useOutletContext<OutletContext>();

    const [priceType, setPriceType] = useState<('currency' | 'crypto' | 'other' | 'free')>('currency');
    const [periods, setPeriods] = useState<any[]>([]);
    const [resources, setResources] = useState<Resource[]>([]);
    const [isDataLoading, setIsDataLoading] = useState<boolean>(false);
    const [dataError, setDataError] = useState<string | null>(null);
    const [selectedCurrency, setSelectedCurrency] = useState<string | null>(null);
    const [selectedPeriod, setSelectedPeriod] = useState<string | null>(periods[0] || null);
    const [priceError, setPriceError] = useState<string | null>(null);
    const [nameError, setNameError] = useState<string | null>(null);
    const [resourcesError, setResourcesError] = useState<string | null>(null);

    const isFirstRender = useRef(true);
    const isFirstTrialCheck = useRef(true);

    const planExists = Object.keys(plan).length > 0;
    const isEditFreePlan = planExists && !isDuplicating && plan?.price?.amount === 0;
    const showPaidSections = !isEditFreePlan && priceType !== 'free';
    const parsedPrice = Number(formData.priceAmount);
    const isPriceInvalid = showPaidSections && (!formData.priceAmount || isNaN(parsedPrice) || parsedPrice <= 0);
    const isNameInvalid = !formData.name || formData.name.trim() === '';

    useEffect(() => {
        if (isEditFreePlan) {
            onFieldChange('isPayAccessCodeOnly', true);
        }
    }, [isEditFreePlan]);

    useEffect(() => {
        if (priceError && !isPriceInvalid) {
            setPriceError(null);
        }
    }, [formData.priceAmount, priceType]);

    useEffect(() => {
        if (nameError && !isNameInvalid) {
            setNameError(null);
        }
    }, [formData.name, isNameInvalid]);

    useEffect(() => {
        if (resourcesError && formData.resourceIds && formData.resourceIds.length > 0) {
            setResourcesError(null);
        }
    }, [formData.resourceIds, resourcesError]);

    const fetchData = async () => {
        setIsDataLoading(true);
        try {
            const response = await fetchAPI('/plan/create-data', auth.user.token, {
                projectId: project.projectId,
            });
            if (!response.ok) throw new Error(`This is an HTTP error: The status is ${response.status}`);
            const { periods, resources } = await response.json();

            if (periods) {
                setPeriods(periods);
            }

            if (resources) {
                setResources(resources);
            }

            setDataError(null);
        } catch (error: any) {
            let errorMessage = 'An unexpected error occurred';
            if (error.response?.message && error.name !== "AbortError") {
                errorMessage = error.response.message;
            }
            setDataError(errorMessage);
        } finally {
            setIsDataLoading(false);
        }
    };

    useEffect(() => {
        if (!planExists) {
            fetchData();
        }
    }, []);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        if (priceType === 'currency' && availableFiatCurrencies?.length > 0) {
            setSelectedCurrency(availableFiatCurrencies[0]);
            onFieldChange('isPayAccessCodeOnly', false);
        } else if (priceType === 'crypto' && availableCryptoCurrencies?.length > 0) {
            setSelectedCurrency(availableCryptoCurrencies[0]);
            onFieldChange('isPayAccessCodeOnly', false);
        } else if (priceType === 'other' && availableOtherCurrencies?.length > 0) {
            setSelectedCurrency(availableOtherCurrencies[0]);
            onFieldChange('isPayAccessCodeOnly', false);
        } else if (priceType === 'free') {
            setSelectedCurrency(null);
            onFieldChange('priceAmount', '0');
            onFieldChange('isPayAccessCodeOnly', true);
            onFieldChange('isNonRecurring', true);
        }
    }, [priceType]);

    useEffect(() => {
        if (periods.length > 0 && periods[0]) {
            setSelectedPeriod(periods[0].period);
            onFieldChange('period', periods[0].period);
        }
    }, [periods]);

    useEffect(() => {
        if (formData.period && formData.period !== selectedPeriod) {
            setSelectedPeriod(formData.period);
        }
    }, [formData.period]);

    useEffect(() => {
        if (isFirstTrialCheck.current) {
            isFirstTrialCheck.current = false;
            return;
        }
        
        if (formData.isTrialOncePerPlan && !(selectedPeriod && parseInt(selectedPeriod, 10) !== lifetime && formData.trialPeriodDays > 0)) {
            onFieldChange('isTrialOncePerPlan', false);
        }
    }, [selectedPeriod, formData.trialPeriodDays]);

    useEffect(() => {
        if (isDuplicating && plan?.price) {
            if (plan.price.amount === 0) {
                setPriceType('free');
                setSelectedCurrency(null);
                onFieldChange('priceAmount', '0');
                onFieldChange('priceCurrency', '');
            } else {
                if (availableFiatCurrencies?.includes(plan.price.currency)) {
                    setPriceType('currency');
                } else if (availableCryptoCurrencies?.includes(plan.price.currency)) {
                    setPriceType('crypto');
                } else if (availableOtherCurrencies?.includes(plan.price.currency)) {
                    setPriceType('other');
                }

                setSelectedCurrency(plan.price.currency);

                onFieldChange('priceAmount', plan.price.amount.toString());
                onFieldChange('priceCurrency', plan.price.currency);
            }
        }
    }, [plan, isDuplicating]);

    const onPriceType = (e: React.ChangeEvent<HTMLInputElement>, type: 'currency' | 'crypto' | 'other' | 'free') => {
        e.preventDefault();
        setPriceType(type)
    }

    const onPrice = (price: string) => {
        onFieldChange('priceAmount', price.toString());
        onFieldChange('priceCurrency', selectedCurrency);
    }

    const onCurrency = (currency: string) => {
        setSelectedCurrency(currency);
        onFieldChange('priceCurrency', currency);
    }

    const onAdvanced = (fieldName: ('isNewcomersOnly' | 'isNonNewcomersOnly' | 'isChurnedOnly')) => {
        const toggledValue = !formData[fieldName];

        onFieldChange(fieldName, toggledValue);

        if (toggledValue) {
            if (fieldName !== 'isNewcomersOnly') {
                onFieldChange('isNewcomersOnly', false);
            }
            if (fieldName !== 'isNonNewcomersOnly') {
                onFieldChange('isNonNewcomersOnly', false);
            }
            if (fieldName !== 'isChurnedOnly') {
                onFieldChange('isChurnedOnly', false);
            }
        }
    }

    const onTrialModeChange = (value: boolean) => {
        if (selectedPeriod && parseInt(selectedPeriod, 10) !== lifetime && formData.trialPeriodDays > 0) {
            onFieldChange('isTrialOncePerPlan', value);
        }
    }

    const scrollToInput = (selector: string) => {
        const el = formRef.current?.querySelector(selector) as HTMLElement | null;
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            el.focus();
        }
    };

    const handleSave = () => {
        if (isNameInvalid) {
            setNameError('Name is required');
            scrollToInput('input[name="plan-name"]');
            return;
        }

        if ((!planExists || isDuplicating) && priceType !== 'free' && isPriceInvalid) {
            setPriceError('Price must be greater than 0');
            scrollToInput('input[name="plan-price-amount"]');
            return;
        }

        if (!formData.resourceIds || formData.resourceIds.length === 0) {
            setResourcesError('Select at least one benefit');
            scrollToInput('input[name^="plan-resource-"]');
            return;
        }

        setPriceError(null);
        setNameError(null);
        setResourcesError(null);
        onSave();
    }

    const handlePeriodChange = (period: any) => {
        setSelectedPeriod(period);
        onFieldChange('period', period);
    }

    return (
        <GenericForm
            formData={formData}
            onFieldChange={onFieldChange}
            onSave={handleSave}
            onUndo={formReset}
            formRef={formRef}
            isLoading={isLoading}
            buttonsVertical
            saveText="Save"
            cancelText="Cancel"
        >
            <div className="md:mb-10 mb-12">
                <div className="text-[13px]/[1.1] md:text-sm/[1.3] font-medium mb-4 md:mb-5">
                    Name and description
                </div>
                <div className="mb-3 md:mb-5">
                    <Input
                        className="h-12"
                        type="text"
                        name="plan-name"
                        value={formData.name || ''}
                        placeholder="Name"
                        onChange={(e) => onFieldChange('name', e.target.value)}
                        required
                        inputClassName={isNameInvalid ? 'border-red-orange focus:border-red-orange' : ''}
                        // value={planExists && plan.name}
                    />
                    {nameError && (
                        <div className="text-sm text-red-orange mt-1 font-medium">{nameError}</div>
                    )}
                </div>
                <FormattingTextarea
                    id="plan-description"
                    value={formData.descriptionMessage?.text || ''}
                    disabled={false}
                    onChange={(text: string, entities: []) => {
                        onFieldChange('descriptionMessage', { ...formData.descriptionMessage, text, entities });
                    }}
                    className="!h-[120px] overflow-y-auto !rounded-[4px] md:!rounded-[6px] bg-transparent border-[#dde1dd] border-t-0 focus:!border-b-link-blue focus:!border-l-link-blue focus:!border-r-link-blue"
                    clearClassName="text-link-blue"
                    entities={formData.descriptionMessage?.entities}
                    hasPro={true}
                    isTrialSubscription={false}
                    messageInput={true}
                    label="Description"
                    labelClassName="flex w-full h-full select-none pointer-events-none absolute left-0 font-normal truncate peer-placeholder-shown:text-blue-gray-500 leading-[1.2] md:leading-none peer-focus:leading-[1.2] md:peer-focus:leading-none peer-disabled:text-transparent peer-disabled:peer-placeholder-shown:text-blue-gray-500 transition-all -top-1.5 peer-placeholder-shown:text-base text-[12px] md:text-[13px] peer-focus:text-[12px] md:peer-focus:text-[13px] before:content[' '] before:block before:box-border before:w-2 md:before:w-[13px] before:h-1.5 before:mt-[5px] before:mr-1 peer-placeholder-shown:before:border-transparent before:rounded-tl-[4px] md:before:rounded-tl-[6px] md:before:translate-y-px before:border-t before:border-l before:pointer-events-none before:transition-all peer-disabled:before:border-transparent after:content[' '] after:block after:flex-grow after:box-border after:w-2.5 after:h-1.5 after:mt-[5px] after:ml-1 peer-placeholder-shown:after:border-transparent after:rounded-tr-[4px] md:after:rounded-tr-[6px] md:after:translate-y-px after:border-t after:border-r after:pointer-events-none after:transition-all peer-disabled:after:border-transparent peer-placeholder-shown:leading-[3.5] text-brand-green/[.5] peer-focus:text-link-blue before:border-[#dde1dd] peer-focus:before:!border-link-blue after:border-[#dde1dd] peer-focus:after:!border-link-blue"
                    buttonActiveClassName="bg-[#F7F6F2]"
                    buttonClassName="transition rounded-md hover:bg-[#F7F6F2]"
                    menuMobileBottom={0}
                    menuMobileBorder="border-t-[0.5px] border-b-[0.5px] border-t-[#F2F2F2] border-b-[#F2F2F2]"
                    menuMobileShadow="shadow-[-2px_-1px_6px_0px_#07366105]"
                    menuShadow="shadow-[0px_0px_8px_0px_#C8C8C81A]"
                />
                <div className="text-xs md:text-[11px] leading-[1.1] text-[#A5A5A5] font-medium mt-1.5 md:mt-2">
                    Users see this description on your membership page and in your bot.
                </div>
            </div>

            {(planExists && !isDuplicating) ? (
                <div className="mb-10">
                    <div className="text-[13px]/[1.1] md:text-sm/[1.3] font-medium mb-1.5 md:mb-2">
                        Price and billing period
                    </div>
                    <div className="text-[13px]/[1.1] md:text-sm/[1.3] text-[#A5A5A5] mb-4 md:mb-5">
                        You cannot change the price and billing period.
                    </div>
                    <Input
                        className="h-12 mb-3 md:mb-5"
                        type="text"
                        name="plan-price"
                        value={plan.price.amount === 0 ? 'FREE' : `${priceView(plan.price.amount, plan.price.currency)} ${plan.price.currency === 'XTR' ? '⭐' : plan.price.currency}`}
                        readOnly
                    />
                    <Input
                        className="h-12"
                        type="text"
                        name="plan-period"
                        value={humanizeDuration(plan.period)}
                        readOnly
                    />
                </div>
            ) : (
                <div>
                    <div className="relative z-[2] mb-10">
                        <div className="text-[13px]/[1.1] md:text-sm/[1.3] font-medium mb-4 md:mb-5">
                            Price
                        </div>
                        <div className="flex flex-wrap gap-2 mb-4 md:mb-5">
                            <div className="relative">
                                <input
                                    id="price-type-currency"
                                    type="radio"
                                    name="price-type"
                                    onChange={(e) => onPriceType(e, 'currency')}
                                    checked={priceType === 'currency'}
                                    value="Currency"
                                    className="sr-only inset-x-0 m-auto h-full"
                                    required
                                />
                                <label
                                    htmlFor="price-type-currency"
                                    className={classNames(
                                        priceType === 'currency' ? 'btn-secondary' : 'btn-secondary-inverted',
                                    )}
                                >Currency</label>
                            </div>
                            <div className="relative">
                                <input
                                    id="price-type-crypto"
                                    type="radio"
                                    name="price-type"
                                    onChange={(e) => onPriceType(e, 'crypto')}
                                    checked={priceType === 'crypto'}
                                    value="Crypto"
                                    className="sr-only inset-x-0 m-auto h-full"
                                    required
                                />
                                <label
                                    htmlFor="price-type-crypto"
                                    className={classNames(
                                        priceType === 'crypto' ? 'btn-secondary' : 'btn-secondary-inverted',
                                    )}
                                >Crypto</label>
                            </div>
                            <div className="relative">
                                <input
                                    id="price-type-other"
                                    type="radio"
                                    name="price-type"
                                    onChange={(e) => onPriceType(e, 'other')}
                                    checked={priceType === 'other'}
                                    value="Telegram Stars"
                                    className="sr-only inset-x-0 m-auto h-full"
                                    required
                                />
                                <label
                                    htmlFor="price-type-other"
                                    className={classNames(
                                        priceType === 'other' ? 'btn-secondary' : 'btn-secondary-inverted',
                                    )}
                                >Telegram Stars</label>
                            </div>
                            <div className="relative">
                                <input
                                    id="price-type-free"
                                    type="radio"
                                    name="price-type"
                                    onChange={(e) => onPriceType(e, 'free')}
                                    checked={priceType === 'free'}
                                    value="Free"
                                    className="sr-only inset-x-0 m-auto h-full"
                                    required
                                />
                                <label
                                    htmlFor="price-type-free"
                                    className={classNames(
                                        priceType === 'free' ? 'btn-secondary' : 'btn-secondary-inverted',
                                    )}
                                >Free</label>
                            </div>
                        </div>
                        {priceType !== 'free' && (
                            <>
                                {availableFiatCurrencies && priceType === 'currency' && (
                                    <div className="flex gap-1.5 md:gap-2">
                                        <div className="w-2/3 md:w-1/2">
                                            <Input
                                                className="h-12"
                                                type="text"
                                                name="plan-price-amount"
                                                placeholder="Price"
                                                onChange={(e) => onPrice(e.target.value)}
                                                value={formData?.priceAmount || ''}
                                                inputClassName={isPriceInvalid ? 'border-red-orange focus:border-red-orange' : ''}
                                                required
                                            />
                                            {priceError && (
                                                <div className="text-sm text-red-orange mt-2 font-medium">{priceError}</div>
                                            )}
                                        </div>
                                        <div className="w-1/3 md:w-1/2">
                                            {availableFiatCurrencies?.length > 0 && (
                                                <DropdownSelect
                                                    options={availableFiatCurrencies.sort().map((currency: any) => ({
                                                        value: currency,
                                                        content: getCurrencyCodeTitle(currency),
                                                        selected: selectedCurrency === currency,
                                                    }))}
                                                    selectSet={(currency: any) => onCurrency(currency)}
                                                    className="w-full h-full transition-all"
                                                    search
                                                />
                                            )}
                                        </div>
                                    </div>
                                )}
                                {availableCryptoCurrencies && priceType === 'crypto' && (
                                    <div className="flex gap-1.5 md:gap-2">
                                        <div className="w-2/3 md:w-1/2">
                                            <Input
                                                className="h-12"
                                                type="text"
                                                name="plan-price-amount"
                                                placeholder="Price"
                                                onChange={(e) => onPrice(e.target.value)}
                                                value={formData?.priceAmount || ''}
                                                inputClassName={isPriceInvalid ? 'border-red-orange focus:border-red-orange' : ''}
                                                required
                                            />
                                            {priceError && (
                                                <div className="text-sm text-red-orange mt-2 font-medium">{priceError}</div>
                                            )}
                                        </div>
                                        <div className="w-1/3 md:w-1/2">
                                            {availableCryptoCurrencies?.length > 0 && (
                                                <DropdownSelect
                                                    options={availableCryptoCurrencies.sort().map((currency: any) => ({
                                                        value: currency,
                                                        content: getCurrencyCodeTitle(currency),
                                                        selected: selectedCurrency === currency,
                                                    }))}
                                                    selectSet={(currency: any) => onCurrency(currency)}
                                                    className="w-full h-full transition-all"
                                                />
                                            )}
                                        </div>
                                    </div>
                                )}
                                {availableOtherCurrencies && priceType === 'other' && (
                                    <div className="flex gap-1.5 md:gap-2">
                                        <div className="w-2/3 md:w-1/2">
                                            <Input
                                                className="h-12"
                                                type="text"
                                                name="plan-price-amount"
                                                placeholder="Price"
                                                onChange={(e) => onPrice(e.target.value)}
                                                value={formData?.priceAmount || ''}
                                                inputClassName={isPriceInvalid ? 'border-red-orange focus:border-red-orange' : ''}
                                                required
                                            />
                                            {priceError && (
                                                <div className="text-sm text-red-orange mt-2 font-medium">{priceError}</div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    <div className="relative z-[1] mb-10">
                        <div className="text-[13px]/[1.1] md:text-sm/[1.3] font-medium mb-4 md:mb-5">
                            Billing period
                        </div>
                        {periods.length > 0 && (
                            <div className="flex">
                                <DropdownSelect
                                    options={periods.map(({ period }: any) => ({
                                        value: period,
                                        content: humanizeDuration(period),
                                        selected: selectedPeriod === period,
                                    }))}
                                    selectSet={handlePeriodChange}
                                    className="w-full h-full transition-all"
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}

            {showPaidSections && (selectedPeriod && parseInt(selectedPeriod, 10) !== lifetime) && (
                <div className="mb-12">
                    <div className="text-[13px]/[1.1] md:text-sm/[1.3] font-medium mb-1.5 md:mb-2">
                        Recurring
                    </div>
                    <div className="text-[13px]/[1.1] md:text-sm/[1.3] text-[#A5A5A5]">
                        Allows you to make payments regular.
                    </div>

                    <label className="flex items-center cursor-pointer mt-6">
                        <input
                            type="checkbox"
                            className="sr-only peer"
                            name="plan-isNonRecurring"
                            checked={Boolean(!formData.isNonRecurring)}
                            onChange={(e) => onFieldChange('isNonRecurring', !formData.isNonRecurring)}
                        />
                        <div className="form-toggle" />
                        <span className="ml-4 text-[13px]/[1.1] md:text-sm/[1.3] font-medium">Recurring payments</span>
                    </label>
                </div>
            )}

            {showPaidSections && (
                <div className="mb-10 md:mb-14">
                    <div className="text-[13px]/[1.1] md:text-sm/[1.3] font-medium mb-1.5 md:mb-2">
                        Trial period
                    </div>
                    <div className="text-[13px]/[1.1] md:text-sm/[1.3] text-[#A5A5A5] mb-4">
                        Please enter plan trial period (days)
                    </div>
                    <Input
                        className="h-12 mb-3 md:mb-5"
                        type="text"
                        name="plan-trial-days"
                        placeholder="Number of days"
                        value={formData.trialPeriodDays || ''}
                        onChange={(e) => onFieldChange('trialPeriodDays', e.target.value ? parseInt(e.target.value, 10) : '')}
                    />
                    {selectedPeriod && parseInt(selectedPeriod, 10) !== lifetime && (
                        <div>
                            <label className="flex items-center cursor-pointer mt-6">
                                <input
                                    type="checkbox"
                                    className="sr-only peer"
                                    name="plan-trialPeriodWithoutCard"
                                    checked={Boolean(formData.trialPeriodWithoutCard)}
                                    disabled={!formData.trialPeriodDays || parseInt(formData.trialPeriodDays, 10) === 0}
                                    onChange={() => onFieldChange('trialPeriodWithoutCard', !formData.trialPeriodWithoutCard)}
                                />
                                <div className="form-toggle" />
                                <span className="ml-4 text-[13px]/[1.1] md:text-sm/[1.3] font-medium">Allow trial period without card</span>
                            </label>
                        </div>
                    )}
                </div>
            )}

            <div className="flex flex-col md:flex-row gap-4 md:gap-10">
                <div className="text-[13px]/[1.1] md:text-sm/[1.3] font-medium">
                    Benefits
                </div>
                <div>
                    {(plan.resources || resources).map((resource: any, index: number) => (
                        <label
                            key={`plan-modal-resource-${index}`}
                            className={classNames(
                                'flex gap-2',
                                index !== (plan.resources || resources).length - 1 && 'mb-2'
                            )}
                        >
                            <input type="checkbox"
                                className="form-checkbox mt-0.5"
                                name={`plan-resource-${index}`}
                                checked={Boolean(formData.resourceIds?.includes(resource.resourceId))}
                                onChange={() => onFieldChange(
                                    `resourceIds`,
                                    formData.resourceIds?.includes(resource.resourceId)
                                        ? formData.resourceIds.filter((id: string) => id !== resource.resourceId)
                                        : [...formData.resourceIds || [], resource.resourceId]
                                )}
                            />
                            <span>{resource.telegramGroupId ? 'Telegram Group' : resource.telegramChannelId ? 'Telegram Channel' : 'Manual'}: {resource.name}</span>
                        </label>
                    ))}
                </div>
                {resourcesError && (
                    <div className="text-sm text-red-orange mt-2 font-medium">{resourcesError}</div>
                )}
            </div>
            <div className="mt-10 md:mt-14 pt-8 border-t border-t-[#E8E8E8]">
                <div className="text-[13px]/[1.1] md:text-sm/[1.3] font-medium mb-1.5 md:mb-2">
                    Advanced settings
                </div>
                <div className="block mt-6">
                    <label className="flex items-center cursor-pointer">
                        <input
                            type="checkbox"
                            name="plan-isNewcomersOnly"
                            className="sr-only peer"
                            checked={Boolean(formData.isNewcomersOnly)}
                            onChange={() => onAdvanced('isNewcomersOnly')}
                        />
                        <div className="form-toggle" />
                        <span className="ml-4 text-[13px]/[1.1] md:text-sm/[1.3] font-medium">Newcomers only</span>
                    </label>
                    <div className="text-[13px]/[1.1] md:text-sm/[1.3] text-[#A5A5A5] mt-3 pr-16">
                        Makes this plan available to <span className="font-semibold">new customers</span> only — those who're making their first payment ever. It's great for converting new users into active customers.
                    </div>
                </div>
                <div className="block mt-8 md:mt-10">
                    <label className="flex items-center cursor-pointer mt-6">
                        <input
                            type="checkbox"
                            name="plan-isNonNewcomersOnly"
                            className="sr-only peer"
                            checked={Boolean(formData.isNonNewcomersOnly)}
                            onChange={() => onAdvanced('isNonNewcomersOnly')}
                        />
                        <div className="form-toggle" />
                        <span className="ml-4 text-[13px]/[1.1] md:text-sm/[1.3] font-medium">Customers only</span>
                    </label>
                    <div className="text-[13px]/[1.1] md:text-sm/[1.3] text-[#A5A5A5] mt-3 pr-16">
                        Makes this plan available to <span className="font-semibold">all active & churned customers</span> — those who've ever made any payments to you (excluding those on trials). It's great for upsells and upgrades of many kinds.
                    </div>
                </div>
                <div className="block mt-8 md:mt-10">
                    <label className="flex items-center cursor-pointer mt-6">
                        <input
                            type="checkbox"
                            name="plan-isChurnedOnly"
                            className="sr-only peer"
                            checked={Boolean(formData.isChurnedOnly)}
                            onChange={() => onAdvanced('isChurnedOnly')}
                        />
                        <div className="form-toggle" />
                        <span className="ml-4 text-[13px]/[1.1] md:text-sm/[1.3] font-medium">Churned only</span>
                    </label>
                    <div className="text-[13px]/[1.1] md:text-sm/[1.3] text-[#A5A5A5] mt-3 pr-16">
                        Makes this plan available to <span className="font-semibold">churned customers</span> — those who don't have an active subscription or payments right now but had them in the past. Use this option to reactivate your "sleeping" customers.
                    </div>
                </div>
                <div className="block mt-8 md:mt-10">
                    <label className="flex items-center cursor-pointer mt-6">
                        <input
                            type="checkbox"
                            name="plan-isPayAccessCodeOnly"
                            className="sr-only peer"
                            checked={isEditFreePlan ? true : Boolean(formData.isPayAccessCodeOnly)}
                            onChange={() => onFieldChange('isPayAccessCodeOnly', !formData.isPayAccessCodeOnly)}
                            disabled={priceType === 'free' || isEditFreePlan}
                        />
                        <div className="form-toggle" />
                        <span className="ml-4 text-[13px]/[1.1] md:text-sm/[1.3] font-medium">Access Code only</span>
                    </label>
                    <div className="text-[13px]/[1.1] md:text-sm/[1.3] text-[#A5A5A5] mt-3 pr-16">
                        You can create subscription plans that don't have a price and can only be activated with an Access Code.
                    </div>
                </div>
                <div className="block mt-8 md:mt-10">
                    <label className="flex items-center cursor-pointer mt-6">
                        <input
                            type="checkbox"
                            name="plan-allowHiddenRenewal"
                            className="sr-only peer"
                            checked={Boolean(formData.allowHiddenRenewal)}
                            onChange={() => onFieldChange('allowHiddenRenewal', !formData.allowHiddenRenewal)}
                        />
                        <div className="form-toggle" />
                        <span className="ml-4 text-[13px]/[1.1] md:text-sm/[1.3] font-medium">Allow disabled plan renewals</span>
                    </label>
                    <div className="text-[13px]/[1.1] md:text-sm/[1.3] text-[#A5A5A5] mt-3 pr-16">
                        Allows members to renew this plan even when it is disabled and hidden from new users.
                    </div>
                </div>
                {selectedPeriod && parseInt(selectedPeriod, 10) !== lifetime && formData.trialPeriodDays > 0 && (
                    <div className="block mt-8 md:mt-10">
                        <div className="text-[13px]/[1.1] md:text-sm/[1.3] font-medium">
                            Trial mode
                        </div>
                        <div className="text-[13px]/[1.1] md:text-sm/[1.3] text-[#A5A5A5] mt-2 pr-16">
                            Choose how the trial period should be applied to the plan.
                        </div>
                        <div className="mt-6">
                            <button className="flex items-center group w-full text-[13px]/[1.1] md:text-sm/[1.3] font-medium text-brand-green outline-none mb-3" onClick={() => onTrialModeChange(true)}>
                                 <div className="shrink-0 mr-2 w-5 h-5 flex items-center justify-center">
                                    <div className={`w-full h-full rounded-full border ${formData.isTrialOncePerPlan ? 'border-brand-green' : 'border-[#E8E8E8] group-hover:border-brand-green'} flex items-center justify-center transition-all duration-200`}>
                                        {formData.isTrialOncePerPlan && (
                                            <div className="w-2.5 h-2.5 rounded-full bg-brand-green"></div>
                                        )}
                                    </div>
                                 </div>
                                 <span className="font-normal">Once per plan</span>
                             </button>
                             <button className="flex items-center group w-full text-[13px]/[1.1] md:text-sm/[1.3] font-medium text-brand-green outline-none" onClick={() => onTrialModeChange(false)}>
                                 <div className="shrink-0 mr-2 w-5 h-5 flex items-center justify-center">
                                    <div className={`w-full h-full rounded-full border ${!formData.isTrialOncePerPlan ? 'border-brand-green' : 'border-[#E8E8E8] group-hover:border-brand-green'} flex items-center justify-center transition-all duration-200`}>
                                        {!formData.isTrialOncePerPlan && (
                                            <div className="w-2.5 h-2.5 rounded-full bg-brand-green"></div>
                                        )}
                                    </div>
                                 </div>
                                 <span className="font-normal">Once per project</span>
                             </button>
                        </div>
                    </div>
                )}
            </div>
        </GenericForm>
    );
}
